////
// Actions
////

// Benchmarks - overarching benchmark data for an organization
export interface BenchmarksRequest {
  organizationUUID: string;
}

export interface BaseBenchmarksRequestAction<T> extends BenchmarksRequest {
  type: T;
}

export interface BenchmarksRequestAction
  extends BaseBenchmarksRequestAction<'BENCHMARKS_REQUEST'> {}

export interface BenchmarksSuccessAction
  extends BaseBenchmarksRequestAction<'BENCHMARKS_SUCCESS'> {
  results: BenchmarkResults;
}

export interface BenchmarksFailureAction
  extends BaseBenchmarksRequestAction<'BENCHMARKS_FAILURE'> {
  error: string;
}

function fetchBenchmarks(organizationUUID: string): BenchmarksRequestAction {
  return {
    type: 'BENCHMARKS_REQUEST',
    organizationUUID,
  };
}

// Metric - daily, by-user, by-repository, and overall data for a single
// metric for org, period, userName?, repositoryUUID?
export interface MetricRequest extends BenchmarksRequest {
  metric: string;
  period: number;
  byReviewer?: boolean;
  userName?: string;
  repositoryUUID?: string;
}

export interface BaseMetricRequestAction<T> extends MetricRequest {
  type: T;
}

export function metricRequestKey(action: MetricRequest): string {
  const {
    organizationUUID,
    metric,
    period,
    byReviewer,
    userName,
    repositoryUUID,
  } = action;
  return [
    organizationUUID,
    metric,
    period.toString(),
    Boolean(byReviewer).toString(),
    userName || '-',
    repositoryUUID || '-',
  ].join(':');
}

export interface MetricRequestAction
  extends BaseMetricRequestAction<'BENCHMARK_METRIC_REQUEST'> {}

export interface MetricSuccessAction
  extends BaseMetricRequestAction<'BENCHMARK_METRIC_SUCCESS'> {
  results: MetricResult;
}

export interface MetricFailureAction
  extends BaseMetricRequestAction<'BENCHMARK_METRIC_FAILURE'> {
  error: string;
}

function fetchMetric(request: MetricRequest): MetricRequestAction {
  return {
    ...request,
    type: 'BENCHMARK_METRIC_REQUEST',
  };
}

export type BenchmarkActions =
  | BenchmarksRequestAction
  | BenchmarksSuccessAction
  | BenchmarksFailureAction
  | MetricRequestAction
  | MetricSuccessAction
  | MetricFailureAction;

export const actions = {
  fetchBenchmarks,
  fetchMetric,
};

export type BaseBenchmarkRequest<T> = {
  fetching: boolean;
  error?: string;
  results?: T;
};

export type BenchmarkResults = {
  benchmarks: Array<BenchmarkResult>;
  cohort: {
    name: string | null;
    uuid: string | null;
  };
  header: {
    lines_reviewed: { current: number; previous: number };
    pull_requests_count: { current: number; previous: number };
    total_repositories_for_org: number;
    total_repositories_for_user: number;
  };
};

export type BenchmarkResult = {
  cohort_insight: string | null;
  cohort_rank: number | null;
  current: number;
  description: string;
  display_name: string;
  improving: boolean;
  info_tooltip: string;
  learn_more_url: string;
  name: string;
  past_performance_insight: string;
  positive_direction: string;
  previous: number;
  quartiles: { lower: number; median: number; upper: number };
  unit: string;
};

type MetricDayResult = {
  date: string;
  value: number | null;
};

type MetricUserResult = {
  user_name: string;
  current: number | null;
  previous: number | null;
};

type MetricRepositoryResult = {
  name: string;
  current: number | null;
  previous: number | null;
};

export type MetricOverallResult = {
  current: number | null;
  previous: number | null;
};

export type MetricMetaEntry = {
  data_type: string;
  slug: string;
  display_name: string;
  unit: string;
  description: string;
  positive_direction: string;
};

export type MetricMeta = {
  by_author: Array<MetricMetaEntry>;
  by_reviewer: Array<MetricMetaEntry>;
};

export type MetricResult = {
  period: {
    from: string;
    type: string;
    value: number;
  };
  by_day: Array<MetricDayResult>;
  by_user: Array<MetricUserResult>;
  by_repository: Array<MetricRepositoryResult>;
  by_reviewer: boolean;
  overall: {
    [key: string]: MetricOverallResult;
  };
  meta: MetricMeta;
};

export type UserResult = {
  user_name: string;
  current: number;
  previous: number;
};

export type BenchmarksState = {
  byOrganization: {
    [uuid: string]: BaseBenchmarkRequest<BenchmarkResults>;
  };
  metrics: {
    [key: string]: BaseBenchmarkRequest<MetricResult>;
  };
  metricsMeta: MetricMeta;
};

export type State = {
  benchmarks: BenchmarksState;
};

const initialState: BenchmarksState = {
  byOrganization: {},
  metrics: {},
  metricsMeta: {
    by_author: [],
    by_reviewer: [],
  },
};

export default (
  state = initialState,
  action: BenchmarkActions
): BenchmarksState => {
  switch (action.type) {
    case 'BENCHMARKS_REQUEST': {
      const existing = state.byOrganization[action.organizationUUID] || {};

      return {
        ...state,
        byOrganization: {
          ...state.byOrganization,
          [action.organizationUUID]: {
            ...existing,
            fetching: true,
          },
        },
      };
    }
    case 'BENCHMARKS_SUCCESS': {
      const existing = state.byOrganization[action.organizationUUID];

      const organization = {
        ...existing,
        fetching: false,
        results: action.results,
      };

      delete organization.error;

      return {
        ...state,
        byOrganization: {
          ...state.byOrganization,
          [action.organizationUUID]: organization,
        },
      };
    }
    case 'BENCHMARKS_FAILURE': {
      const existing = state.byOrganization[action.organizationUUID];

      return {
        ...state,
        byOrganization: {
          ...state.byOrganization,
          [action.organizationUUID]: {
            ...existing,
            fetching: false,
            error: action.error,
          },
        },
      };
    }

    case 'BENCHMARK_METRIC_REQUEST': {
      const key = metricRequestKey(action);

      return {
        ...state,
        metrics: {
          ...state.metrics,
          [key]: { fetching: true },
        },
      };
    }
    case 'BENCHMARK_METRIC_SUCCESS': {
      const key = metricRequestKey(action);
      const { results } = action;

      return {
        ...state,
        metrics: {
          ...state.metrics,
          [key]: { fetching: false, results },
        },
        metricsMeta: {
          ...state.metricsMeta,
          by_author: results.meta.by_author,
          by_reviewer: results.meta.by_reviewer,
        },
      };
    }
    case 'BENCHMARK_METRIC_FAILURE': {
      const key = metricRequestKey(action);
      const { error } = action;

      return {
        ...state,
        metrics: {
          ...state.metrics,
          [key]: { fetching: false, error },
        },
      };
    }

    default:
      return state;
  }
};

export const selectors = {
  benchmarksByOrganization:
    (uuid: string) => (state: { benchmarks: BenchmarksState }) => {
      if (
        state.benchmarks.byOrganization[uuid] &&
        state.benchmarks.byOrganization[uuid].results
      ) {
        return state.benchmarks.byOrganization[uuid].results;
      } else {
        return null;
      }
    },
  isFetchingBenchmarks:
    (uuid: string) => (state: { benchmarks: BenchmarksState }) => {
      const benchmarks = state.benchmarks.byOrganization[uuid];
      return benchmarks && benchmarks.fetching;
    },
  metric:
    (request: MetricRequest) => (state: { benchmarks: BenchmarksState }) => {
      const key = metricRequestKey(request);
      const existing = state.benchmarks.metrics[key];
      return existing || null;
    },
  metricsMeta: (state: { benchmarks: BenchmarksState }) => {
    return state.benchmarks.metricsMeta;
  },
  isMetricsLoading: (state: { benchmarks: BenchmarksState }) => {
    const { by_author, by_reviewer } = state.benchmarks.metricsMeta;

    return by_author.length === 0 && by_reviewer.length === 0;
  },
  getMetricDisplayName:
    (metric: string, listByReviewer: boolean) =>
    (state: { benchmarks: BenchmarksState }) => {
      const listType = listByReviewer ? 'by_reviewer' : 'by_author';
      const metricMetaEntry = state.benchmarks.metricsMeta[listType].find(
        (m) => m.slug === metric
      );
      return metricMetaEntry?.display_name;
    },
};
